import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  Paper,
  Chip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Pagination,
  CircularProgress,
} from '@mui/material';
import { Account, Invoice } from '../../../types';
import { formatCurrency, getStatusColor, daysRemaining } from '../../../utils/helpers';
import { isValidEmail, isValidPhoneNumber } from '../../../utils/validation';
import PhoneInput from '../../../components/common/PhoneInput';
import InvoiceCard from './InvoiceCard';
import AccountNotes from './AccountNotes';
import LaunchIcon from '@mui/icons-material/Launch';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';

import { useAppDispatch } from '../../../redux/hooks';
import { updateAccount, fetchAccounts, fetchAccountSummary, fetchAccountById } from '../../../redux/slices/accountsSlice';
import { apiService } from '../../../services/api';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

// Error Boundary Component
class InvoiceErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Invoice rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Invoices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There was an error rendering the invoices. Please try refreshing the page.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Error: {this.state.error?.message}
          </Typography>
        </Paper>
      );
    }

    return this.props.children;
  }
}

interface AccountDetailProps {
  account: Account;
  onClose: () => void;
}

const AccountDetail: React.FC<AccountDetailProps> = ({ account, onClose }) => {
  const theme = useTheme();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedAccount, setEditedAccount] = useState<Account>({ 
    ...account,
    invoices: account.invoices || [] 
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [showJsonData, setShowJsonData] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({
    contactEmail: false,
    contactNumber: false,
    contactID: false,
    hsID: false
  });

  // Invoice filtering and search state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('dueDate');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Show 10 invoices per page
  const [error, setError] = useState<string | null>(null);
  
  // Add defensive checks for all properties
  const {
    contactID = '',
    hsID = '',
    name = '',
    balance = 0,
    creditLimit = 0,
    availableCredit = 0,
    paymentTerm = 0,
    accountStatus = 'Active',
    invoices = [],
  } = account || {};

  // Determine status color
  const statusColor = getStatusColor(accountStatus);

  // Reset edited account when original account changes
  useEffect(() => {
    try {
      setEditedAccount({ 
        ...account,
        invoices: account.invoices || [] 
      });
      setError(null);
    } catch (err) {
      console.error('Error setting edited account:', err);
      setError('Failed to load account data');
    }
  }, [account]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm, sortBy]);

  // Check if agreement exists for this account
  useEffect(() => {
    const checkAgreement = async () => {
      if (contactID) {
        try {
          setCheckingAgreement(true);
          const exists = await apiService.hasAgreement(contactID);
          setHasAgreement(exists);
        } catch (error) {
          console.error('Error checking agreement:', error);
          setHasAgreement(false);
        } finally {
          setCheckingAgreement(false);
        }
      }
    };

    checkAgreement();
  }, [contactID]);

  // Memoized filtered invoices to prevent infinite re-renders
  const filteredInvoices = useMemo(() => {
    try {
      setError(null);
      
      // Validate invoices array
      if (!Array.isArray(invoices)) {
        console.error('Invoices is not an array:', invoices);
        setError('Invalid invoice data format');
        return [];
      }

      // Use all invoices - handle missing data gracefully in rendering
      let filtered = [...invoices]; // Create a copy to avoid mutations

      // Apply status filter
      if (statusFilter !== 'all') {
        filtered = filtered.filter(invoice => {
          try {
            const upperStatus = (invoice.status || '').toUpperCase();
            
            // Only calculate daysToPayment if dueDate exists
            let daysToPayment = 0;
            if (invoice.dueDate) {
              daysToPayment = daysRemaining(invoice.dueDate);
            }
            
            switch (statusFilter) {
              case 'paid':
                return upperStatus === 'PAID';
              case 'voided':
                return upperStatus === 'VOIDED' || upperStatus === 'DELETED';
              case 'overdue':
                return invoice.dueDate && daysToPayment < 0 && upperStatus !== 'PAID' && upperStatus !== 'VOIDED' && upperStatus !== 'DELETED';
              case 'awaiting':
                return (!invoice.dueDate || daysToPayment >= 0) && upperStatus !== 'PAID' && upperStatus !== 'VOIDED' && upperStatus !== 'DELETED';
              default:
                return true;
            }
          } catch (error) {
            console.error('Error filtering invoice:', invoice, error);
            return true; // Include invoice if filtering fails
          }
        });
      }

      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(invoice => {
          try {
            const name = invoice.name || '';
            const invoiceNumber = invoice.invoiceNumber || '';
            const invoiceID = invoice.invoiceID || '';
            
            return (
              name.toLowerCase().includes(searchLower) ||
              invoiceNumber.toLowerCase().includes(searchLower) ||
              invoiceID.toLowerCase().includes(searchLower)
            );
          } catch (error) {
            console.error('Error searching invoice:', invoice, error);
            return true; // Include invoice if search fails
          }
        });
      }

      // Apply sorting
      filtered.sort((a, b) => {
        try {
          switch (sortBy) {
            case 'dueDate':
              const dueDateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
              const dueDateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
              return dueDateA - dueDateB;
            case 'date':
              const dateA = a.date ? new Date(a.date).getTime() : 0;
              const dateB = b.date ? new Date(b.date).getTime() : 0;
              return dateA - dateB;
            case 'amount':
              return (b.amountDue || 0) - (a.amountDue || 0);
            case 'name':
              const nameA = a.name || a.invoiceNumber || '';
              const nameB = b.name || b.invoiceNumber || '';
              return nameA.localeCompare(nameB);
            default:
              return 0;
          }
        } catch (error) {
          console.error('Error sorting invoices:', a, b, error);
          return 0;
        }
      });

      return filtered;
    } catch (error) {
      console.error('Critical error in getFilteredInvoices:', error);
      setError(`Error processing invoices: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [];
    }
  }, [invoices, statusFilter, searchTerm, sortBy]);
  
  // Calculate pagination - memoized to prevent recalculation
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedInvoices = filteredInvoices.slice(startIndex, endIndex);
    
    return {
      totalPages,
      startIndex,
      endIndex,
      paginatedInvoices
    };
  }, [filteredInvoices, currentPage, itemsPerPage]);

  // Handle pagination change
  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
    try {
      setCurrentPage(value);
      // Scroll to top of invoices section
      const invoicesSection = document.getElementById('invoices-section');
      if (invoicesSection) {
        invoicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (err) {
      console.error('Error changing page:', err);
    }
  }, []);

  const dispatch = useAppDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hasAgreement, setHasAgreement] = useState(false);
  const [checkingAgreement, setCheckingAgreement] = useState(true);

  // Handle dialog open/close
  const handleOpenEditDialog = useCallback(() => {
    setEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof Account, value: any) => {
    setEditedAccount(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  }, []);

  // Handle status toggle
  const handleStatusToggle = useCallback(() => {
    const newStatus = editedAccount.accountStatus === 'Suspended' ? 'Active' : 'Suspended';
    handleFieldChange('accountStatus', newStatus);
  }, [editedAccount.accountStatus, handleFieldChange]);

  // Handle save
  const handleSave = useCallback(async () => {
    setIsUploading(true);
    
    try {
      // Validate required fields
      const errors = {
        contactEmail: editedAccount.contactEmail ? !isValidEmail(editedAccount.contactEmail) : false,
        contactNumber: editedAccount.contactNumber ? !isValidPhoneNumber(editedAccount.contactNumber) : false,
        contactID: !editedAccount.contactID,
        hsID: !editedAccount.hsID
      };

      setValidationErrors(errors);

      // Check if there are any validation errors
      if (Object.values(errors).some(error => error)) {
        setSnackbarMessage('Please fix validation errors before saving');
        setSnackbarOpen(true);
        return;
      }

      // Call the API to update the account
      const result = await dispatch(updateAccount(editedAccount));

      if (updateAccount.fulfilled.match(result)) {
        // If there's an agreement file, upload it
        if (agreementFile && editedAccount.contactID) {
          try {
            await apiService.uploadAgreement(editedAccount.contactID, agreementFile);
            setHasAgreement(true); // Update agreement status
            setSnackbarMessage('Account and agreement updated successfully');
          } catch (uploadError) {
            console.error('Failed to upload agreement:', uploadError);
            setSnackbarMessage('Account updated successfully, but failed to upload agreement. Please try again.');
          }
        } else {
          setSnackbarMessage('Account updated successfully');
        }
        
        setSnackbarOpen(true);
        setEditDialogOpen(false);
        setHasChanges(false);
        setAgreementFile(null);
        
        // Refresh the accounts list and account summary in the background
        dispatch(fetchAccounts());
        dispatch(fetchAccountSummary());
        
        // The account data is already updated from the updateAccount thunk
        // No need to fetch again as the Redux state is already current
        // The component will automatically re-render with the updated data
      } else {
        setSnackbarMessage('Failed to update account. Please try again.');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error saving account:', error);
      setSnackbarMessage('An error occurred while saving. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setIsUploading(false);
    }
  }, [editedAccount, account.contactID, dispatch, agreementFile]);
  
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  // Check if fields should be disabled
  const isFieldDisabled = editedAccount.accountStatus === 'Suspended';

  // Handle external system links
  const handleOpenHubspot = useCallback(() => {
    window.open(`https://app.hubspot.com/contacts/19996504/record/0-2/${hsID}`, '_blank');
  }, [hsID]);

  const handleOpenXero = useCallback(() => {
    window.open(`https://go.xero.com/app/!byhWs/contacts/contact/${contactID}`, '_blank');
  }, [contactID]);

  const handleViewInvoiceInXero = useCallback((invoiceID: string) => {
    window.open(`https://go.xero.com/app/!byhWs/invoicing/view/${invoiceID}`, '_blank');
  }, []);

  // Handle view agreement
  const handleViewAgreement = useCallback(async () => {
    try {
      const blob = await apiService.downloadAgreement(contactID);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing agreement:', error);
      setSnackbarMessage('Failed to load agreement. Please try again.');
      setSnackbarOpen(true);
    }
  }, [contactID]);

  // If there's a critical error, show error state
  if (error) {
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Account Detail Error
          </Typography>
          <Button variant="outlined" onClick={onClose}>
            Back to Accounts
          </Button>
        </Box>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Error Loading Account
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2 }}>
            Refresh Page
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={accountStatus}
              size="small"
              sx={{
                backgroundColor: `${statusColor}20`,
                color: statusColor,
                fontWeight: 'bold',
              }}
            />
            <Typography variant="body2" color="text.secondary">
              ID: {account.id || 'N/A'} | Xero ID: {contactID}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<EditIcon />}
            onClick={handleOpenEditDialog}
          >
            Edit
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Back to Accounts
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Balance
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {formatCurrency(balance)}
                </Typography>
              </Box>
              
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Credit Limit
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {creditLimit === 99999999 ? 'Unlimited' : formatCurrency(creditLimit)}
                </Typography>
              </Box>
              
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Available Credit
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {formatCurrency(availableCredit)}
                </Typography>
              </Box>
              
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Payment Term
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {paymentTerm} days
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Contact Name
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {account.contactName || 'N/A'}
                </Typography>
              </Box>
              
              <Box sx={{ width: 'calc(50% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Contact Number
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {account.contactNumber || 'N/A'}
                </Typography>
              </Box>
              
              <Box sx={{ width: 'calc(100% - 8px)' }}>
                <Typography variant="body2" color="text.secondary">
                  Contact Email
                </Typography>
                <Typography variant="h6" gutterBottom>
                  {account.contactEmail || 'N/A'}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                External Systems
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  onClick={handleOpenHubspot}
                >
                  Open in Hubspot
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  onClick={handleOpenXero}
                >
                  Open in Xero
                </Button>
                {!checkingAgreement && hasAgreement && (
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleViewAgreement}
                    color="secondary"
                  >
                    View Agreement
                  </Button>
                )}
                {checkingAgreement && (
                  <Button
                    variant="outlined"
                    disabled
                    startIcon={<CircularProgress size={16} />}
                  >
                    Checking...
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
        
        <Box sx={{ flex: 1 }}>
          <AccountNotes accountID={contactID} />
        </Box>
      </Box>

      <InvoiceErrorBoundary>
        <Box id="invoices-section" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Invoices ({filteredInvoices.length} total)
            </Typography>
            {filteredInvoices.length > itemsPerPage && (
              <Typography variant="body2" color="text.secondary">
                Showing {paginationData.startIndex + 1}-{Math.min(paginationData.endIndex, filteredInvoices.length)} of {filteredInvoices.length}
              </Typography>
            )}
          </Box>
          
          {/* Filters and Search */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center' }}>
              {/* Search */}
              <TextField
                placeholder="Search by invoice name or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flex: 1, minWidth: 250 }}
              />
              
              {/* Status Filter */}
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(e, newFilter) => newFilter && setStatusFilter(newFilter)}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="awaiting">Awaiting Payment</ToggleButton>
                <ToggleButton value="overdue">Overdue</ToggleButton>
                <ToggleButton value="paid">Paid</ToggleButton>
                <ToggleButton value="voided">Voided</ToggleButton>
              </ToggleButtonGroup>
              
              {/* Sort By */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="dueDate">Due Date</MenuItem>
                  <MenuItem value="date">Invoice Date</MenuItem>
                  <MenuItem value="amount">Amount Due</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>
          
          <Divider sx={{ mb: 3 }} />
          
          {/* Invoice cards */}
          {paginationData.paginatedInvoices && paginationData.paginatedInvoices.length > 0 ? (
            <>
              {paginationData.paginatedInvoices.map((invoice: Invoice, index: number) => {
                try {
                  return (
                    <InvoiceCard
                      key={`${invoice.invoiceID || 'unknown'}-${invoice.invoiceNumber || 'unknown'}-${index}`}
                      invoice={invoice}
                      onViewInXero={handleViewInvoiceInXero}
                    />
                  );
                } catch (err) {
                  console.error('Error rendering invoice card:', invoice, err);
                  return (
                    <Paper key={`error-${index}`} sx={{ p: 2, mb: 2, bgcolor: 'error.light' }}>
                      <Typography variant="body2" color="error">
                        Error rendering invoice: {invoice.invoiceNumber || 'Unknown'}
                      </Typography>
                    </Paper>
                  );
                }
              })}
              
              {/* Pagination */}
              {paginationData.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={paginationData.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No invoices match your current filters.' 
                  : 'No invoices found for this account.'}
              </Typography>
            </Paper>
          )}
        </Box>
      </InvoiceErrorBoundary>
      
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="md" fullWidth>
        <DialogTitle>Edit Account</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={editedAccount.accountStatus !== 'Suspended'}
                  onChange={handleStatusToggle}
                  color="primary"
                />
              }
              label={`Account Status: ${editedAccount.accountStatus}`}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {editedAccount.accountStatus === 'Suspended' 
                ? "Account is suspended. Toggle to activate." 
                : "Account is active. Toggle to suspend."}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Details
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Database ID: {editedAccount.id || 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="name"
                  value={editedAccount.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Xero ID (Contact ID)"
                  name="contactID"
                  value={editedAccount.contactID}
                  onChange={(e) => handleFieldChange('contactID', e.target.value)}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                  required
                  error={validationErrors.contactID}
                  helperText={validationErrors.contactID ? "Xero ID is required" : ""}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="HubSpot ID"
                  name="hsID"
                  value={editedAccount.hsID}
                  onChange={(e) => handleFieldChange('hsID', e.target.value)}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                  required
                  error={validationErrors.hsID}
                  helperText={validationErrors.hsID ? "HubSpot ID is required" : ""}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Credit Limit"
                  type="number"
                  value={editedAccount.creditLimit === 99999999 ? 'Unlimited' : editedAccount.creditLimit}
                  onChange={(e) => handleFieldChange('creditLimit', Number(e.target.value))}
                  disabled={isFieldDisabled || editedAccount.creditLimit === 99999999}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: <Typography variant="body1">£</Typography>,
                    readOnly: editedAccount.creditLimit === 99999999
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editedAccount.creditLimit === 99999999}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleFieldChange('creditLimit', 99999999);
                        } else {
                          handleFieldChange('creditLimit', 5000); // Default value
                        }
                      }}
                      color="primary"
                      disabled={isFieldDisabled}
                    />
                  }
                  label="No Credit Limit"
                  sx={{ mt: 1 }}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Payment Term"
                  type="number"
                  value={editedAccount.paymentTerm}
                  onChange={(e) => handleFieldChange('paymentTerm', Number(e.target.value))}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    endAdornment: <Typography variant="body2">days</Typography>
                  }}
                  inputProps={{
                    min: 0,
                    step: 1
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          <Box>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={editedAccount.contactName || ''}
                  onChange={(e) => handleFieldChange('contactName', e.target.value)}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' } }}>
                <PhoneInput
                  label="Contact Number"
                  value={editedAccount.contactNumber || ''}
                  onChange={(value: string) => handleFieldChange('contactNumber', value)}
                  disabled={isFieldDisabled}
                  error={validationErrors.contactNumber}
                  helperText={validationErrors.contactNumber ? "Please enter a valid phone number" : ""}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  value={editedAccount.contactEmail || ''}
                  onChange={(e) => handleFieldChange('contactEmail', e.target.value)}
                  disabled={isFieldDisabled}
                  margin="normal"
                  variant="outlined"
                  error={validationErrors.contactEmail}
                  helperText={validationErrors.contactEmail ? "Please enter a valid email address" : ""}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Credit Agreement
            </Typography>
            <Button
              variant="outlined"
              component="label"
              disabled={isFieldDisabled}
              sx={{ mt: 1 }}
            >
              Upload Agreement (PDF)
              <input
                type="file"
                accept=".pdf"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.type !== 'application/pdf') {
                      setSnackbarMessage('Please select a PDF file');
                      setSnackbarOpen(true);
                      return;
                    }
                    if (file.size > 10 * 1024 * 1024) {
                      setSnackbarMessage('File size must be less than 10MB');
                      setSnackbarOpen(true);
                      return;
                    }
                    setAgreementFile(file);
                    setHasChanges(true);
                  }
                }}
              />
            </Button>
            {agreementFile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Selected: {agreementFile.name}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Upload a PDF credit agreement for this account. Maximum file size: 10MB.
            </Typography>
          </Box>

          {isFieldDisabled && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Account is suspended. Activate the account to enable editing.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {hasChanges && (
            <Button 
              onClick={handleSave} 
              color="primary" 
              variant="contained"
              disabled={validationErrors.contactEmail || validationErrors.contactNumber || validationErrors.contactID || validationErrors.hsID || isUploading}
              startIcon={isUploading ? <CircularProgress size={20} /> : (agreementFile ? <CloudUploadIcon /> : undefined)}
            >
              {isUploading ? 'Saving...' : 'Save'}
            </Button>
          )}
          <Button onClick={handleCloseEditDialog} disabled={isUploading}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default AccountDetail;
